import React from 'react';
import { format, parseISO } from 'date-fns';

const userLang = navigator.language || navigator.userLanguage;

// Payment status color classes
const statusColorClasses = {
  green: 'bg-green-100 text-green-800 border-green-200',
  red: 'bg-red-100 text-red-800 border-red-200',
  orange: 'bg-orange-100 text-orange-800 border-orange-200',
  yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  blue: 'bg-blue-100 text-blue-800 border-blue-200',
  gray: 'bg-gray-100 text-gray-800 border-gray-200'
};

// Get payment status information from order
const getPaymentStatus = (order) => {
  const status = order.status?.toLowerCase();
  
  switch (status) {
    case 'pending':
      return { 
        status: 'pending', 
        label: userLang === 'zh-CN' ? '待付款' : 'Payment Pending', 
        color: 'yellow' 
      };
    
    case 'payment_failed':
      return { 
        status: 'payment_failed', 
        label: userLang === 'zh-CN' ? '付款失败' : 'Payment Failed', 
        color: 'red',
        timestamp: order.updatedAt || order.updated_at
      };
    
    case 'paid':
    case 'completed':
      return { 
        status: 'paid', 
        label: userLang === 'zh-CN' ? '已付款' : 'Paid', 
        color: 'green',
        timestamp: order.updatedAt || order.updated_at
      };
    
    case 'refund_pending':
      return { 
        status: 'refund_pending', 
        label: userLang === 'zh-CN' ? '退款处理中' : 'Refund Pending', 
        color: 'orange',
        timestamp: order.returnApprovedAt || order.updatedAt || order.updated_at
      };
    
    case 'partially_refunded':
      return { 
        status: 'partially_refunded', 
        label: userLang === 'zh-CN' ? '部分退款' : 'Partially Refunded', 
        color: 'orange',
        timestamp: order.returnCompletedAt || order.updatedAt || order.updated_at
      };
    
    case 'fully_refunded':
      return { 
        status: 'fully_refunded', 
        label: userLang === 'zh-CN' ? '已退款' : 'Fully Refunded', 
        color: 'orange',
        timestamp: order.returnCompletedAt || order.updatedAt || order.updated_at
      };
    
    case 'deposit_pending_refund':
      return { 
        status: 'deposit_pending_refund', 
        label: userLang === 'zh-CN' ? '押金退款中' : 'Deposit Refund Pending', 
        color: 'yellow',
        timestamp: order.returnApprovedAt || order.updatedAt || order.updated_at
      };
    
    case 'deposit_refunded':
      return { 
        status: 'deposit_refunded', 
        label: userLang === 'zh-CN' ? '押金已退' : 'Deposit Refunded', 
        color: 'green',
        timestamp: order.returnCompletedAt || order.updatedAt || order.updated_at
      };
    
    case 'canceled':
      return { 
        status: 'canceled', 
        label: userLang === 'zh-CN' ? '已取消' : 'Canceled', 
        color: 'red',
        timestamp: order.canceledAt || order.updatedAt || order.updated_at
      };
    
    // Legacy fallback for rental-specific status  
    case 'refunded':
      if (order.depositRefunded && order.orderType === 'rental') {
        return { 
          status: 'deposit_refunded', 
          label: userLang === 'zh-CN' ? '押金已退' : 'Deposit Refunded', 
          color: 'green',
          timestamp: order.returnCompletedAt || order.updatedAt || order.updated_at
        };
      }
      return { 
        status: 'refunded', 
        label: userLang === 'zh-CN' ? '已退款' : 'Refunded', 
        color: 'orange',
        timestamp: order.returnCompletedAt || order.updatedAt || order.updated_at
      };
    
    default:
      return { 
        status: 'unknown', 
        label: userLang === 'zh-CN' ? '未知状态' : 'Unknown', 
        color: 'gray' 
      };
  }
};

const PaymentStatusBadge = ({ order, showTimestamp = false, className = '' }) => {
  if (!order) {
    return null;
  }

  const paymentStatus = getPaymentStatus(order);
  const colorClass = statusColorClasses[paymentStatus.color] || statusColorClasses.gray;

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${colorClass}`}>
        {paymentStatus.label}
      </span>
      {showTimestamp && paymentStatus.timestamp && (
        <span className="text-xs text-gray-500 mt-1">
          {format(parseISO(paymentStatus.timestamp), 'PPp')}
        </span>
      )}
    </div>
  );
};

export default PaymentStatusBadge; 